import { Languages } from '../@types/types'

export const DE = 'de'
export const EN = 'en'

const LOCALE_PATTERN: Record<Languages, RegExp> = {
  de: /^[-+]?\d+(\.\d{3})*(,\d+)?$/,
  en: /^[-+]?\d+(,\d{3})*(\.\d+)?$/,
}

const LOCALE_FORMAT = {
  de: (value: string) => value.split('.').join('').replace(',', '.'),
  en: (value: string) => value.split(',').join('').replace('.', ','),
}

export const isLocalizedDecimal = (value: string, lang: Languages) => LOCALE_PATTERN[lang].test(value)

export const parseDecimal = (value: string | number | undefined, lang: Languages): string | undefined => {
  if (value === undefined) return undefined
  if (value === '') return ''
  if (typeof value === 'number') return LOCALE_FORMAT['en'](String(value)).replace('+', '')
  if (!isLocalizedDecimal(String(value), lang))
    throw new Error(`Expected localized decimal string, but found ${String(value)} for lang ${lang}`)

  let trimmed = LOCALE_FORMAT[lang](String(value)).replace('+', '')
  while (trimmed.startsWith('0') && trimmed.length > 1 && trimmed[1] !== '.' && trimmed[1] !== ',') {
    trimmed = trimmed.slice(1)
  }

  return trimmed
}

// this type mimics the Intl.NumberFormatOptions type that is not exported
type PossibleNumberStyles = {
  decimal: never
  currency: never
  percent: never
}

export const formatDecimal = (
  value: string,
  lang: Languages,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  style?: keyof PossibleNumberStyles
): string => {
  if (isNaN(Number(value))) return ''
  const local = lang + '-' + lang.toUpperCase()
  return new Intl.NumberFormat(local, {
    style,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(Number(value))
}

export const formatNumberForDisplay = (value: number) => formatDecimal(Number(value).toFixed(2), DE, 2, 2)

export const formatNumberForDisplayWithOneDecimalPosition = (value: number): string =>
  formatDecimal(Number(value).toFixed(1), DE, 0, 1)

export const formatNumberForCalcWithDefault = (
  value: string | undefined,
  defaultValue: number | null = 0
): number | null => {
  if (value === '' || value == null) {
    return defaultValue
  }
  try {
    return Number(parseDecimal(value, DE))
  } catch (e) {
    return defaultValue
  }
}

export const formatNumberForCalc = (value: string | undefined): number => {
  return <number>formatNumberForCalcWithDefault(value, 0)
}

export const formatNumber = (value: number | undefined | null): string | undefined => {
  if (value === undefined || value === null) {
    return undefined
  }
  return value.toString().replace('.', ',')
}
